import { I18nNamespace } from 'constants/i18n.const';
import { PHOTO_PAGE } from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { formatMoneyVN } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { diffFromDate } from 'helpers/time.helper';
import { useModal } from 'hooks/useModal';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { useTranslation } from 'hooks/useTranslation';
import { find, toNumber } from 'lodash';
import { ApplyVoucherCodeResponseModel } from 'models/photo/bill-acceptor.model';
import { PaymentMethodEnum } from 'models/photo/transaction.model';
import moment from 'moment';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCheckVoucherCodeApiActionMutation,
  useCreateMomoTransactionApiActionMutation,
  useCreateVNPayTransactionApiActionMutation,
} from 'store/api/payment.slice-api';
import { useFirstPushTransactionApiActionMutation } from 'store/api/transaction.slice.api';
import { useAppSelector } from 'store/store-hooks';

export function useCheckPayment() {
  const { T } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const navigate = useNavigate();
  const { currentLayout } = useBoothAppContext();
  const { audio } = useSoundContext();
  const { openConfirmModal, closeConfirmModal, openErrorModal } = useModal();
  const { getNextPath } = useNewPhotoLifeCycleStep();
  const [firstPushTransactionApiAction] =
    useFirstPushTransactionApiActionMutation();
  const [createMomoTransactionApiAction] =
    useCreateMomoTransactionApiActionMutation();
  const [createVNPayTransactionApiAction] =
    useCreateVNPayTransactionApiActionMutation();
  const [checkVoucherCodeApiAction] = useCheckVoucherCodeApiActionMutation();

  const {
    quantitySheet,
    transactionId,
    depositAmount,
    appliedVoucherCode,
    appliedVoucherValue,
    momoPaymentAmount,
    momoTransactionExpireDate,
    isChooseQRCodeToPay,
    paymentMethodForTest,
    isChooseVNPayToPay,
  } = useAppSelector((state) => state.selfeApp);
  const { isTurnOnVNPayMethodPayment, isTurnOnMomoMethodPayment } =
    useAppSelector((s) => s.booth);

  const costOfPayment = useMemo(() => {
    if (paymentMethodForTest > 0) return 0;

    return (
      find(currentLayout?.prices, (o) =>
        isEqualVal(o?.numberOfPicture, quantitySheet),
      )?.price ||
      currentLayout?.prices?.[0]?.price ||
      0
    );
  }, [currentLayout?.prices, quantitySheet, paymentMethodForTest]);

  const getErrorMessageCreateQR = (res: any) => {
    if (res?.error || res?.data?.data?.resultCode !== 0) {
      return res?.error?.data?.Message || res?.data?.data?.message;
    }
  };

  const handleFirstSaveTransaction = () => {
    // Lưu transaction
    firstPushTransactionApiAction({
      id: transactionId,
      voucherCode: appliedVoucherCode,
      deposit: depositAmount - appliedVoucherValue,
      paymentMethod: isChooseQRCodeToPay
        ? PaymentMethodEnum.MOMO
        : PaymentMethodEnum.CASH,
      ...(isChooseVNPayToPay && {
        paymentMethod: PaymentMethodEnum.VNPAY,
      }),
      // Nếu trong chế độ test thử
      ...(paymentMethodForTest > 0 && {
        deposit: 0,
        paymentMethod: paymentMethodForTest,
      }),
    });
  };

  const handleCreateMomoQRCode = async (amount = 0) =>
    // create transaction
    createMomoTransactionApiAction({
      OrderInfo: T(`${I18nNamespace.COMMON}:funTransaction`),
      OrderId: `${transactionId}_${moment().unix()}`,
      Amount: amount || costOfPayment - depositAmount,
    }).then((res: any) => {
      if (getErrorMessageCreateQR(res)) {
        openErrorModal(getErrorMessageCreateQR(res) || undefined);
      }
      return res;
    });

  const handleCreateVNPayQRCode = async (amount = 0) =>
    // create transaction
    createVNPayTransactionApiAction({
      OrderInfo: T(`${I18nNamespace.COMMON}:funTransaction`),
      OrderId: `${transactionId}_${moment().unix()}`,
      // OrderId: generateUniqueOrderId(),
      Amount: amount || costOfPayment - depositAmount,
    }).then((res: any) => {
      if (getErrorMessageCreateQR(res)) {
        openErrorModal(getErrorMessageCreateQR(res) || undefined);
      }
      return res;
    });

  const handleCheckMinPaymentUseVoucherIsValid = async () => {
    if (!appliedVoucherCode) return true;

    try {
      await checkVoucherCodeApiAction({
        code: appliedVoucherCode,
        price: costOfPayment,
      }).unwrap();

      return true;
    } catch (error) {
      openConfirmModal({
        content: `${T('page:minTransactionToApplyVoucher', {
          minValue: formatMoneyVN(
            toNumber(
              (error as { data: ApplyVoucherCodeResponseModel })?.data?.data
                ?.minimum,
            ) || 0,
          ),
        })}`,
        onOk: closeConfirmModal,
      });
      return false;
    }
  };

  // User click thanh toán bằng QRCode
  const handleCheckBeforeCreateQRPayment = async () => {
    if (!(await handleCheckMinPaymentUseVoucherIsValid())) return;
    if (depositAmount < costOfPayment) {
      let isCanNext = true;
      if (
        momoPaymentAmount !== Math.abs(costOfPayment - depositAmount) ||
        !diffFromDate(moment().toDate(), momoTransactionExpireDate)
      ) {
        // nếu link qrcode momo hết hạn hoặc momoTransactionExpireDate = null
        // tạo link thanh toán MoMo mới
        let qrRes: any;

        if (isTurnOnVNPayMethodPayment) {
          qrRes = await handleCreateVNPayQRCode();
        } else if (isTurnOnMomoMethodPayment) {
          qrRes = await handleCreateMomoQRCode();
        }
        if (getErrorMessageCreateQR(qrRes)) isCanNext = false;
      }
      if (isCanNext) {
        audio?.playContinueClick?.();
        navigate(getNextPath() || '');
      }
    } else if (depositAmount > costOfPayment) {
      audio?.playRedundantDeposit?.();
      openConfirmModal({
        content: `${T('page:changeMoneyWarning')}`,
        onOk: () => {
          audio?.playContinueClick?.();
          handleFirstSaveTransaction();
          closeConfirmModal?.();
          navigate(getNextPath(null, [PHOTO_PAGE.QR_CODE]) || '');
        },
      });
    } else {
      audio?.playContinueClick?.();
      handleFirstSaveTransaction();
      navigate(getNextPath(null, [PHOTO_PAGE.QR_CODE]) || '');
    }
  };

  // User click khi đủ tiền
  const handleBeforeNextWarningAboutMoney = async () => {
    if (!(await handleCheckMinPaymentUseVoucherIsValid())) return;
    if (depositAmount < costOfPayment) return;
    if (depositAmount > costOfPayment) {
      audio?.playRedundantDeposit?.();
      openConfirmModal({
        content: `${T('page:changeMoneyWarning')}`,
        onOk: () => {
          audio?.playContinueClick?.();
          handleFirstSaveTransaction();
          closeConfirmModal?.();
          navigate(getNextPath() || '');
        },
      });
    } else {
      audio?.playContinueClick?.();
      handleFirstSaveTransaction();
      navigate(getNextPath() || '');
    }
  };

  return {
    costOfPayment,
    isEnoughDeposit: depositAmount >= costOfPayment,
    handleBeforeNextWarningAboutMoney,
    handleCheckBeforeCreateQRPayment,
    handleCreateMomoQRCode,
    handleCreateVNPayQRCode,
  };
}
